const parseTimeMinutes = (value) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || ''));
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 24 || minutes < 0 || minutes > 59 || (hours === 24 && minutes !== 0)) return null;
  return hours * 60 + minutes;
};

const formatTimeMinutes = (value) => {
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const eventInterval = (event) => {
  const start = parseTimeMinutes(event?.time);
  const duration = Number(event?.duration || 60);
  if (start === null || !Number.isFinite(duration) || duration <= 0) return null;
  return [start, start + duration];
};

const belongsToSameProvider = (googleEvent, appointment) => (
  !googleEvent.psychologist_id
  || !appointment.psychologist_id
  || googleEvent.psychologist_id === appointment.psychologist_id
);

/**
 * Google FreeBusy also contains appointments that ProPraxis has synchronized
 * to Google. Cut those intervals out so one client appointment is not painted
 * twice. Partial overlaps are split, preserving real Google-only busy time.
 */
export const subtractAppointmentsFromGoogleBusy = (googleBusy = [], appointments = []) => {
  const internalAppointments = appointments.filter(
    appointment => !appointment.is_external_busy && appointment.status !== 'cancelled',
  );

  return googleBusy.flatMap((googleEvent) => {
    const googleInterval = eventInterval(googleEvent);
    if (!googleInterval) return [googleEvent];

    const overlaps = internalAppointments
      .filter(appointment => (
        appointment.date === googleEvent.date
        && belongsToSameProvider(googleEvent, appointment)
      ))
      .map(eventInterval)
      .filter(Boolean)
      .sort((a, b) => a[0] - b[0]);

    let segments = [googleInterval];
    overlaps.forEach(([appointmentStart, appointmentEnd]) => {
      segments = segments.flatMap(([segmentStart, segmentEnd]) => {
        if (appointmentEnd <= segmentStart || appointmentStart >= segmentEnd) {
          return [[segmentStart, segmentEnd]];
        }
        return [
          [segmentStart, Math.min(appointmentStart, segmentEnd)],
          [Math.max(appointmentEnd, segmentStart), segmentEnd],
        ].filter(([start, end]) => end > start);
      });
    });

    if (
      segments.length === 1
      && segments[0][0] === googleInterval[0]
      && segments[0][1] === googleInterval[1]
    ) {
      return [googleEvent];
    }

    return segments.map(([start, end]) => ({
      ...googleEvent,
      id: `${googleEvent.id}:visible:${start}:${end}`,
      time: formatTimeMinutes(start),
      duration: end - start,
    }));
  });
};
